import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Users, Search, Shield, ShieldCheck, ShieldAlert, Copy, Check } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserManagementPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createdUserDialog, setCreatedUserDialog] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedCode, setCopiedCode] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role: "staff"
  });

  const isAdminOrSuperAdmin = user?.role === "admin" || user?.role === "super_admin";

  useEffect(() => {
    if (isAdminOrSuperAdmin) {
      fetchUsers();
    }
  }, [isAdminOrSuperAdmin]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/users`, formData);
      setCreatedUser(response.data);
      setDialogOpen(false);
      setCreatedUserDialog(true);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/users/${userId}/role?role=${newRole}`);
      toast.success("Role updated successfully");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/users/${selectedUser.id}`);
      toast.success("User deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      role: "staff"
    });
  };

  const copyUserCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
    toast.success("Login code copied!");
  };

  const getRoleBadge = (role) => {
    const config = {
      staff: { style: "border-blue-500/50 text-blue-400 bg-blue-500/10", icon: Shield, label: "Staff" },
      admin: { style: "border-green-500/50 text-green-400 bg-green-500/10", icon: ShieldCheck, label: "Admin" },
      super_admin: { style: "border-purple-500/50 text-purple-400 bg-purple-500/10", icon: ShieldAlert, label: "Super Admin" }
    };
    return config[role] || config.staff;
  };

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.user_code && u.user_code.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (u.email && u.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    u.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isAdminOrSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <ShieldAlert className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-heading font-bold text-foreground">Access Denied</h2>
          <p className="text-muted-foreground mt-2">Only Admin and Super Admin can access User Management</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-primary animate-pulse font-heading text-xl">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="user-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3">
            <Users className="w-8 h-8 text-purple-400" />
            User Management
          </h1>
          <p className="text-muted-foreground mt-1">Manage system users and their roles</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="glow-primary bg-primary hover:bg-primary/90"
          data-testid="add-user-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Staff</p>
                <p className="font-heading text-2xl font-bold text-blue-400">
                  {users.filter(u => u.role === "staff").length}
                </p>
              </div>
              <Shield className="w-8 h-8 text-blue-400/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Admin</p>
                <p className="font-heading text-2xl font-bold text-green-400">
                  {users.filter(u => u.role === "admin").length}
                </p>
              </div>
              <ShieldCheck className="w-8 h-8 text-green-400/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Super Admin</p>
                <p className="font-heading text-2xl font-bold text-purple-400">
                  {users.filter(u => u.role === "super_admin").length}
                </p>
              </div>
              <ShieldAlert className="w-8 h-8 text-purple-400/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="border-border bg-card">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search users by name, login code, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-secondary/50 border-border"
              data-testid="user-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-border bg-card">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="font-heading">All Users ({filteredUsers.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Login Code</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Name</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Email</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Role</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Created</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No users found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((u) => {
                    const roleConfig = getRoleBadge(u.role);
                    const RoleIcon = roleConfig.icon;
                    return (
                      <TableRow key={u.id} className="border-border table-row-hover" data-testid={`user-row-${u.id}`}>
                        <TableCell className="font-mono text-primary font-bold">
                          <div className="flex items-center gap-2">
                            {u.user_code || '-'}
                            {u.user_code && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => copyUserCode(u.user_code)}
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium text-foreground">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                              <span className="text-primary font-semibold text-sm">
                                {u.name?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            {u.name}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">{u.email || '-'}</TableCell>
                        <TableCell>
                          <Select 
                            value={u.role} 
                            onValueChange={(newRole) => handleRoleChange(u.id, newRole)}
                            disabled={u.id === user?.id}
                          >
                            <SelectTrigger className="w-[140px] bg-transparent border-0 p-0 h-auto" data-testid={`role-select-${u.id}`}>
                              <Badge variant="outline" className={`${roleConfig.style} cursor-pointer`}>
                                <RoleIcon className="w-3 h-3 mr-1" />
                                {roleConfig.label}
                              </Badge>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="staff">
                                <div className="flex items-center gap-2">
                                  <Shield className="w-4 h-4 text-blue-400" />
                                  Staff
                                </div>
                              </SelectItem>
                              <SelectItem value="admin">
                                <div className="flex items-center gap-2">
                                  <ShieldCheck className="w-4 h-4 text-green-400" />
                                  Admin
                                </div>
                              </SelectItem>
                              <SelectItem value="super_admin">
                                <div className="flex items-center gap-2">
                                  <ShieldAlert className="w-4 h-4 text-purple-400" />
                                  Super Admin
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {new Date(u.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {u.id !== user?.id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => { setSelectedUser(u); setDeleteDialogOpen(true); }}
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add New User</DialogTitle>
            <DialogDescription>Create a new staff or admin user. A unique Login Code will be generated automatically.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="John Doe"
                required
                data-testid="user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email (Optional)</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="john@ardhanageshwar.com"
                data-testid="user-email-input"
              />
              <p className="text-xs text-muted-foreground">Required for password reset via email</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger className="bg-secondary/50 border-border" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-blue-400" />
                      Staff - Can only enter data
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-green-400" />
                      Admin - Can create & verify
                    </div>
                  </SelectItem>
                  <SelectItem value="super_admin">
                    <div className="flex items-center gap-2">
                      <ShieldAlert className="w-4 h-4 text-purple-400" />
                      Super Admin - Full access
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="p-3 bg-secondary/30 rounded-lg border border-border">
              <p className="text-sm text-muted-foreground">
                <strong>Note:</strong> Default password for all new users is <span className="font-mono font-bold text-primary">000000</span>
              </p>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="glow-primary bg-primary" data-testid="user-submit-btn">
                Create User
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* User Created Success Dialog */}
      <Dialog open={createdUserDialog} onOpenChange={setCreatedUserDialog}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-green-400 flex items-center gap-2">
              <Check className="w-5 h-5" />
              User Created Successfully!
            </DialogTitle>
            <DialogDescription>Share these credentials with the new user</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-secondary/30 rounded-lg border border-border space-y-3">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Login Code</p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="font-mono text-2xl font-bold text-primary">{createdUser?.user_code}</p>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => copyUserCode(createdUser?.user_code)}
                  >
                    {copiedCode ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Default Password</p>
                <p className="font-mono text-xl font-bold text-foreground mt-1">000000</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Name</p>
                <p className="font-medium text-foreground mt-1">{createdUser?.name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Role</p>
                <p className="font-medium text-foreground mt-1 capitalize">{createdUser?.role?.replace('_', ' ')}</p>
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground text-center">
              The user can log in with their Login Code and password 000000
            </p>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setCreatedUserDialog(false)} className="w-full glow-primary bg-primary">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedUser?.name}" ({selectedUser?.user_code || selectedUser?.email})? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              data-testid="confirm-delete-user"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagementPage;
